import { useEffect } from 'react'

/**
 * Hook that re-initializes iubenda preference links after Footer component mounts
 * 
 * The iubenda script loads in <head> and scans the DOM immediately, but the React Footer
 * component (containing iubenda-cs-preferences-link and iubenda-cs-uspr-link elements)
 * mounts later. This causes the links to not be detected.
 * 
 * Solution: Call activateSnippets() after Footer mounts to re-scan the DOM and
 * activate the preference links.
 * 
 * Note: floatingPreferencesButtonDisplay is set to false in index.html to hide
 * the default floating widget, as we use footer links instead.
 * 
 * @see https://www.iubenda.com/en/help/1205-cookie-solution-customization/#activateSnippets
 */
export const useIubenda = () => {
  useEffect(() => {
    // Defensive check: ensure iubenda API is loaded
    if (typeof window !== 'undefined' && window._iub && window._iub.cs && window._iub.cs.api) {
      try {
        // Re-scan DOM to detect and activate iubenda preference links
        // This is necessary because Footer mounts after iubenda's initial DOM scan
        window._iub.cs.api.activateSnippets()
        
        // Log in development
        if (import.meta.env.DEV) {
          console.log('[iubenda] Re-scanned DOM - preference links activated')
        }
      } catch (error) {
        // Fail silently in production, log in development
        if (import.meta.env.DEV) {
          console.error('[iubenda] Failed to activate preference links:', error)
        }
      }
    } else if (import.meta.env.DEV) {
      console.warn('[iubenda] API not available - preference links may not work')
    }
  }, []) // Run only once after component mounts
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    _iub?: {
      cs?: {
        api?: {
          activateSnippets: (options?: { runOnActivationDoneCallback?: boolean }) => void
          openPreferences: () => void
          isConsentGiven: () => boolean
        }
        ui?: {
          showPPUsprSection: (openInNewWindow?: boolean) => void
        }
      }
    }
  }
}
